import { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie'; // Import useCookies hook
import Logo from '../../components/logo/Logo';
import {
  Box,
  Button,
  Heading,
  PasswordInput,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import ApiService from '../../services/ApiService';
import OtpInputPage from './OtpInput';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import ReCAPTCHA from 'react-google-recaptcha';
import { setCardDetails } from '../../store/slices/authDetails';
import { setMerchants, setSelectedMerchant } from '@store/slices/merchantSlice';
import { getMerchantsWithLoginFallback } from '@src/utils/merchantHelpers';
import { getErrorMessage, getErrorStatus } from '@src/utils/errorMessage';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

const Login = () => {
  const dispatch = useDispatch();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const { handleLogin, handleLogout, isAuthenticated } = useAuth();
  const [recaptchaVal, setRecaptchaVal] = useState<null | string>(null);
  const [, setCookie] = useCookies([
    'authToken',
    'refreshToken',
    'loungeId',
    'loungeDetails',
    'role',
  ]);
  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const isProduction = import.meta.env.VITE_APP_ENV === 'production';

  const handleCaptchaVerification = (value: string | null) => {
    if (value) {
      setRecaptchaVal(value);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setLoading(true);
      const response = await ApiService.post('v1/user/staff/login', {
        username: username,
        password: password,
        ...(isProduction && { captchaToken: recaptchaVal }),
      });

      const payload = response.data ?? response;
      if (payload.access_token) {
        const { access_token, refresh_token, member_details } = payload;

        dispatch(setCardDetails(member_details));

        // Set cookies for session data
        setCookie('authToken', access_token, {
          path: '/',
          secure: true, // Ensure secure cookies in production
          sameSite: 'strict',
        });
        setCookie('refreshToken', refresh_token, {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });
        setCookie('loungeId', member_details?.lounge_id, {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });
        setCookie('loungeDetails', JSON.stringify(member_details?.lounge), {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });
        setCookie('role', member_details?.role, {
          path: '/',
          secure: true,
          sameSite: 'strict',
        });

        // Primary: use merchants from login response; fallback: call merchant API
        setLoadingMerchants(true);
        const { merchants, error } = await getMerchantsWithLoginFallback(
          member_details?.merchants as
            | import('@src/types/merchant').Merchant[]
            | undefined,
        );
        setLoadingMerchants(false);

        if (!merchants.length) {
          if (error) {
            const status = getErrorStatus(error);
            if (status === 403) {
              toast.error(
                'Access Denied: You do not have permission to access any merchant. Please contact your administrator.',
                { autoClose: 5000 },
              );
            } else if (status === 401) {
              toast.error('Authentication failed. Please log in again.');
            } else {
              toast.error(
                getErrorMessage(
                  error,
                  'Failed to load merchants after multiple attempts.',
                ),
                { autoClose: 5000 },
              );
            }
          } else {
            toast.error(
              'No active merchants found for your account. Please contact support.',
              { autoClose: 5000 },
            );
          }
          setLoading(false);
          handleLogout();
          return;
        }

        dispatch(setMerchants(merchants));
        dispatch(setSelectedMerchant(merchants[0]));

        handleLogin();
        navigate('/', { state: { ...payload } });
      } else {
        setLoading(false);
        setStep(2);
      }
    } catch (error: unknown) {
      setLoading(false);
      setLoadingMerchants(false);
      const msg = getErrorMessage(error);
      setError(msg);
      toast.error(msg, { autoClose: 5000 });
      console.error('Login error:', error);
    }
  };

  const handleReturn = () => {
    setStep(0);
    setUsername('');
    setPassword('');
    setError('');
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  return (
    <Box
      display="flex"
      justifyContent="center"
      height="100vh"
      overflowY="auto"
      padding="spacing.6"
    >
      <Box display="flex" flexDirection="column" gap="spacing.4" width="602px">
        <Box paddingTop="spacing.10">
          <Logo />
        </Box>
        {step === 0 && (
          <form onSubmit={handleSubmit} data-testid="login-form">
            <Box display="flex" flexDirection="column" gap="spacing.6">
              <Heading
                size="xlarge"
                textAlign="center"
                color="surface.text.primary.normal"
              >
                Login credentials
              </Heading>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="2xlarge"
                backgroundColor="surface.background.gray.intense"
                paddingX="spacing.8"
                paddingY="spacing.8"
                elevation="lowRaised"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="spacing.6"
                  width={{ base: '100%', m: '506px' }}
                >
                  <TextInput
                    label="Username"
                    name="username"
                    placeholder="Enter your username"
                    value={username}
                    onChange={({ value }) => setUsername(value ?? '')}
                    isRequired
                    autoCompleteSuggestionType="username"
                    testID="login-username-input"
                    size="large"
                  />
                  <PasswordInput
                    label="Password"
                    name="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={({ value }) => setPassword(value ?? '')}
                    isRequired
                    showRevealButton
                    autoCompleteSuggestionType="password"
                    testID="login-password-input"
                    size="large"
                  />
                  {isProduction && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      gap="spacing.4"
                    >
                      <ReCAPTCHA
                        sitekey={RECAPTCHA_SITE_KEY || ''}
                        onChange={handleCaptchaVerification}
                      />
                      <Text size="small" color="surface.text.gray.muted">
                        Please complete the CAPTCHA verification to continue
                      </Text>
                    </Box>
                  )}
                  {/* {error && (
                    <p className="text-red-500 font-medium mb-4">{error}</p>
                  )} */}
                  <Button
                    variant="primary"
                    type="submit"
                    isDisabled={
                      password.length <= 0 ||
                      username.length <= 0 ||
                      (isProduction && !recaptchaVal)
                    }
                    isLoading={loading || loadingMerchants}
                    isFullWidth
                    size="large"
                    testID="login-submit-button"
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            </Box>
          </form>
        )}
        {step === 2 && (
          <OtpInputPage
            username={username}
            password={password}
            onBackToLogin={handleReturn}
          ></OtpInputPage>
        )}
      </Box>
    </Box>
  );
};

export default Login;
